import React, { useState } from 'react';
import { OfferDetailsData, TotalsData } from '@/pages/hettwer/types/OfferDetailsData';

import { OfferHeader } from '@/pages/hettwer/offer/blocks/OfferHeader';
import { CustomerBlock } from '@/pages/hettwer/offer/blocks/CustomerBlock';
import { SummaryBlock } from '@/pages/hettwer/offer/blocks/SummaryBlock';
import { PositionsBlock } from '@/pages/hettwer/offer/blocks/PositionBlock';
import { TotalsBlock } from '@/pages/hettwer/offer/blocks/TotalsBlock';
import { Files } from '@/components/file/Files';
import { PositionData } from '@/pages/hettwer/types/PositionData';
import { OfferCustomerView } from '@/pages/hettwer/offer/blocks/OfferCustomerView.tsx';

interface OfferContentProps {
  offer: OfferDetailsData;
  activePage: string;
  loading: boolean;
  setActivePage: (page: string) => void;
  onOfferChange: (offer: OfferDetailsData) => void;
}

const recalcTotalsFromPositions = (
  positions: PositionData[],
  currentTotals: TotalsData
): TotalsData => {
  const net = positions.reduce((sum, p) => sum + (p.pricing?.total_price ?? 0), 0);
  const vatRate = currentTotals.vat_rate ?? 19;
  const vat = Math.round(net * (vatRate / 100) * 100) / 100;

  return {
    ...currentTotals,
    net_total: Math.round(net * 100) / 100,
    vat_total: vat,
    gross_total: Math.round((net + vat) * 100) / 100
  };
};

const OfferContent: React.FC<OfferContentProps> = ({
  offer,
  activePage,
  loading,
  onOfferChange,
  setActivePage
}) => {
  const [customerViewOpen, setCustomerViewOpen] = useState(false);

  if (loading) {
    return <div>Angebot wird geladen …</div>;
  }

  const isDraft = offer.status === 'draft';

  if (activePage === 'customer') {
    setTimeout(() => {
      setCustomerViewOpen(true);
      setActivePage('overview');
    }, 0);
  }

  return (
    <>
      {customerViewOpen && (
        <OfferCustomerView offer={offer} onClose={() => setCustomerViewOpen(false)} />
      )}

      <div className="grid grid-cols-1 gap-7.5">
        {activePage === 'overview' && (
          <>
            <OfferHeader offer={offer} onChange={onOfferChange} />
            <CustomerBlock
              customer={offer.customer}
              onChange={(customer) => onOfferChange({ ...offer, customer })}
            />
            <SummaryBlock
              summary={offer.summary}
              onChange={(summary) => onOfferChange({ ...offer, summary })}
            />
            <Files title="Dateien" items={offer.files || []} />
          </>
        )}

        {activePage === 'positions' && (
          <>
            <PositionsBlock
              positions={offer.positions}
              isDraft={isDraft}
              onChange={(positions) =>
                onOfferChange({
                  ...offer,
                  positions,
                  totals: isDraft
                    ? recalcTotalsFromPositions(positions, offer.totals)
                    : offer.totals
                })
              }
            />
            <TotalsBlock
              totals={offer.totals}
              positions={offer.positions}
              isDraft={isDraft}
              onChange={(totals) => onOfferChange({ ...offer, totals })}
            />
          </>
        )}
      </div>
    </>
  );
};

export { OfferContent };
